import React, { useMemo, useState, useEffect } from "react";
import { 
  Container,
  Box,
  Typography,
  useTheme,
  Fade,
  Grid,
  Avatar,
  Divider,
  Tabs,
  Tab
} from "@mui/material";
import { 
  Person,
  Security,
  AccountBalanceWallet,
  LoginOutlined,
  Settings,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import PersonalInfoSection from "../../components/profile/PersonalInfoSection";
import AccountSettingsSection from "../../components/profile/AccountSettingsSection";
import SecurityOverviewSection from "../../components/profile/SecurityOverviewSection";
import FinancialPreferencesSection from "../../components/profile/FinancialPreferencesSection";
import { PageContainer, ModernCard, GradientButton } from "../../components/common/StyledComponents";
import { useAuth } from "../../auth/AuthContext";
import { LOGIN_ROUTE_PATH, PROFILE_TAB_QUERY_PARAM, PROFILE_TAB_PERSONAL, PROFILE_TAB_SECURITY, PROFILE_TAB_FINANCIAL, PROFILE_TAB_SETTINGS } from "../../utils/constants/AppConstants";

const TAB_KEYS = [
  PROFILE_TAB_PERSONAL,
  PROFILE_TAB_SETTINGS,
  PROFILE_TAB_SECURITY,
  PROFILE_TAB_FINANCIAL,
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserProfilePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(PROFILE_TAB_QUERY_PARAM) || "";
  const keyFromIndex = (index: number): string => {
    return TAB_KEYS[index] || PROFILE_TAB_PERSONAL;
  };
  const initialIndex = useMemo(() => {
    const indexFromKey = (key: string): number => {
      const idx = TAB_KEYS.indexOf(key);
      return idx >= 0 ? idx : 0;
    };

    return indexFromKey(tabParam);
  }, [tabParam]);
  const [activeTab, setActiveTab] = useState(initialIndex);
  const { isAuthenticated } = useAuth();

  const profileSections = [
    {
      title: "Personal Information",
      description: "Manage your basic profile details",
      icon: <Person />,
      color: theme.palette.primary.main,
    },
    {
      title: "Account Settings",
      description: "Manage your account settings",
      icon: <Settings />,
      color: theme.palette.secondary.main
    },
    {
      title: "Security",
      description: "Manage password and security settings",
      icon: <Security />,
      color: theme.palette.warning.main,
    },
    {
      title: "Financial Preferences",
      description: "Set your currency and financial settings",
      icon: <AccountBalanceWallet />,
      color: theme.palette.success.main,
    },
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set(PROFILE_TAB_QUERY_PARAM, keyFromIndex(newValue));
    setSearchParams(newParams, { replace: true });
  };

  const handleCardClick = (index: number) => {
    setActiveTab(index);
    const newParams = new URLSearchParams(searchParams);
    newParams.set(PROFILE_TAB_QUERY_PARAM, keyFromIndex(index));
    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    // Sync tab when URL query changes externally
    setActiveTab(initialIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIndex]);

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
          <Box sx={{ mb: 4 }}>
            <Person sx={{ fontSize: '4rem', color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Profile Settings
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
              Manage your account information, preferences, and security settings. Please sign in to access your profile.
            </Typography>
            <GradientButton
              variant="contained"
              startIcon={<LoginOutlined />}
              onClick={() => navigate(LOGIN_ROUTE_PATH)}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                px: 4,
                py: 1.5,
              }}
            >
              Sign In to Continue
            </GradientButton>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Fade in={true} timeout={600}>
          <Box>
            {/* Header Section */}
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    fontSize: '2rem',
                    fontWeight: 700,
                  }}
                >
                  <Person sx={{ fontSize: '2.5rem' }} />
                </Avatar>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                    }}
                  >
                    Profile Settings
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Manage your account information and preferences
                  </Typography>
                </Box>
              </Box>
              
              {/* Profile Sections Overview - Now Clickable */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {profileSections.map((section, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <ModernCard
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        background: activeTab === index 
                          ? `linear-gradient(135deg, ${section.color}20 0%, ${section.color}10 100%)`
                          : `linear-gradient(135deg, ${section.color}10 0%, ${section.color}05 100%)`,
                        border: activeTab === index
                          ? `2px solid ${section.color}`
                          : `1px solid ${section.color}20`,
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 35px ${section.color}25`,
                          background: `linear-gradient(135deg, ${section.color}15 0%, ${section.color}08 100%)`,
                        },
                        transition: 'all 0.3s ease-in-out',
                      }}
                      onClick={() => handleCardClick(index)}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          backgroundColor: activeTab === index ? section.color + '25' : section.color + '15',
                          mb: 2,
                        }}
                      >
                        {React.cloneElement(section.icon, {
                          sx: { fontSize: '1.8rem', color: section.color },
                        })}
                      </Box>
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: activeTab === index ? 700 : 600, 
                          color: section.color 
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {section.description}
                      </Typography>
                    </ModernCard>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Tabbed Content */}
            <ModernCard sx={{ overflow: 'hidden' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }
                  }}
                >
                  {profileSections.map((section, index) => (
                    <Tab
                      key={index}
                      label={section.title}
                      icon={section.icon}
                      iconPosition="start"
                      sx={{
                        '&.Mui-selected': {
                          color: section.color,
                        }
                      }}
                    />
                  ))}
                </Tabs>
              </Box>

              <TabPanel value={activeTab} index={0}>
                <PersonalInfoSection />
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <AccountSettingsSection/>
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <SecurityOverviewSection />
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <FinancialPreferencesSection />
              </TabPanel>
            </ModernCard>
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default UserProfilePage;
